import { useQuery } from "@tanstack/react-query";
import { api, ApiEnvelope } from "../../api/client";

/** RM Analysis / Lot QC overview (Ajax/AjaxRm_analysis.php) */
export interface RmAnalysisRow {
  RmLotID: number; LotNumber: string; PurchaseDate: string; CreatedDateTime: string; PurchaseType: string | null;
  PurchaseCount: number | string; PurchaseWeight: number | string; AbStatus: string; AbColour: string;
  Status: string; StatusColour: string; Remarks: string | null; OrgnDetailsPreview: number; Action: "add" | "preview" | "print";
}
export const rmAnalysisApi = {
  list: async (p: { Month: string; Year: string }) => (await api.get<ApiEnvelope<RmAnalysisRow[]>>("/qc/analysis", { params: p })).data,
};

/** Organic / internal check report (RmReportCreate.php) — up to 3 checks per lot */
export interface OrganicCheckRow {
  ReportID: number; LotID: number; PostedDate: string; CheckedCount: string; BlackGills: string; Soft: string;
  DroopingHead: string; BlackSpot: string; Broken: string; ImproperLegs: string; Bends: string; BigPieces: string;
  SmallPieces: string; FitForHonPack: string; Remarks: string;
}
export const organicCheckApi = {
  list: async (lotId: string) => (await api.get<ApiEnvelope<OrganicCheckRow[]>>(`/qc/analysis/${lotId}/checks`)).data,
  post: async (lotId: string, body: Record<string, unknown>) => (await api.post<ApiEnvelope & { ReportCount: number; ReadyForVerification: boolean }>(`/qc/analysis/${lotId}/checks`, body)).data,
};

/** Final verification (RmAnalysisPreview.php) */
export interface VerificationRow { ReportID: number; LotID: number; Remarks: string; PostedUserID: number; SuggestedDeductions: string; ActualDeductions: string }
export const verificationApi = {
  get: async (lotId: string) => (await api.get<ApiEnvelope<VerificationRow | null>>(`/qc/analysis/${lotId}/verification`)).data,
  post: async (lotId: string, body: Record<string, unknown>) => (await api.post<ApiEnvelope>(`/qc/analysis/${lotId}/verification`, body)).data,
};

/** RM AB (Antibiotic) Report — DailyLabReports.php (list) + RmAbReport.php (detail/create) */
export interface AbReportListRow { AbRepID: number; AnalysisDate: string; ReportDate: string; TotalReports: number; DayTotal: number; NightTotal: number; AnalysisDateRaw: string }
export interface AbOption { OptID: number; Category: string; Name: string; Value: string; Colour: string }
export interface LabTechnologist { LUID: number; LbuName: string }
export interface UnreportedLot { RmLotID: number; LotNumber: string }
export interface AbReportEntry { AbRepID: number; AnalysisDate: string; ReportDate: string; LotID: number; LotNumber: string; Parameter: string; Result: string; AOZ: string; SEM: string; CAP: string; AHD: string; AMOZ: string; Sulphite: string; Remarks: string; Shift: string; TestedBy: number; AbStatus: number }
export const abReportApi = {
  list: async (p: { Month: string; Year: string }) => (await api.get<ApiEnvelope<AbReportListRow[]>>("/qc/ab-reports", { params: p })).data,
  dropdowns: async () => (await api.get<ApiEnvelope<{ Options: AbOption[]; LabTechnologists: LabTechnologist[] }>>("/qc/ab-reports/dropdowns")).data,
  unreportedLots: async (p: { Month: string; Year: string }) => (await api.get<ApiEnvelope<UnreportedLot[]>>("/qc/ab-reports/unreported-lots", { params: p })).data,
  byDate: async (AnalysisDate: string) => (await api.get<ApiEnvelope<AbReportEntry[]>>("/qc/ab-reports/by-date", { params: { AnalysisDate } })).data,
  post: async (body: Record<string, unknown>) => (await api.post<ApiEnvelope<{ AbRepID: number }>>("/qc/ab-reports", body)).data,
};

export function useAbReportDropdowns() {
  return useQuery({ queryKey: ["qc", "ab-dropdowns"], queryFn: abReportApi.dropdowns, staleTime: 5 * 60_000 });
}

/** Masters: Lab Names (QcEiaLabNames) / Analysis Required (QcTestList) */
export interface LabNameRow { SNO: number; LabId: number; LabName: string; Address: string; Description: string; Status: number }
export const labNamesApi = {
  list: async () => (await api.get<ApiEnvelope<LabNameRow[]>>("/qc/masters/lab-names")).data,
  post: async (body: Record<string, unknown>) => (await api.post<ApiEnvelope<LabNameRow>>("/qc/masters/lab-names", body)).data,
};

export interface AnalysisRequiredRow { SNO: number; TestID: number; TestCategory: string; TestName: string }
export const analysisRequiredApi = {
  list: async () => (await api.get<ApiEnvelope<AnalysisRequiredRow[]>>("/qc/masters/analysis-required")).data,
  post: async (body: Record<string, unknown>) => (await api.post<ApiEnvelope<AnalysisRequiredRow>>("/qc/masters/analysis-required", body)).data,
};

/** Farmer/Farm Traceability registry (QcTracebilityList) */
export interface TraceabilityRow { SNO: number; TraceID: number; FarmerName: string; AddressContinued: string; TraceNumber: string; Tracebility: string; PondNumber: string; RegAuthority: string; FarmPhoneNumber: string }
export const traceabilityApi = {
  list: async (SearchKey?: string) => (await api.get<ApiEnvelope<TraceabilityRow[]>>("/qc/traceability", { params: SearchKey ? { SearchKey } : {} })).data,
  post: async (body: Record<string, unknown>) => (await api.post<ApiEnvelope<TraceabilityRow>>("/qc/traceability", body)).data,
};
